import { typographyTokens } from '../../../theme/typographyTokens';
import React, { useState, useMemo } from 'react';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { transformQuillListToNested, nestedListStyles } from '../../../utils/quillHtmlTransform';
import HoveredIconConfirm from '../HoveredIconConfirmButton';
import DataFieldOptionCard from './DataFieldOptionCard';
import { DataFieldType } from '../../../../shared/types/DataField';
import { useDeleteDataFieldTemplateMutation } from '../../../services/dataFieldsSlice';
import ModalWidget from '../../ModalWidget/ModalWidget';
import AddDataField from './AddDataField';
import HoveredIcon from '../HoveredIconButton';

const type: Record<DataFieldType, string> = {
  input: 'Input',
  textarea: 'Text Area',
  number: 'Number',
  email: 'Email',
  emailLookup : 'Email Lookup',
  apmLookup:'APM Lookup',
  select: 'Select',
  multiSelect: 'Multi Select',
  date: 'Date',
  dateTime: 'Date Time',
  yesNo: 'Yes/No',
  checkbox: 'Checkbox',
  toggle: 'Toggle',
  tileSelect: 'Tile Select',
  tileMultiSelect: 'Tile Multi Select',
  textbox: 'Text box'
};

const calculateTotalProps = (datafield: {
  type: DataFieldType;
  dataFieldOptions: any[];
}) => {
  const dataFieldNum = datafield.dataFieldOptions.length;

  const selectedType = type[datafield.type] ?? datafield.type;
  return `${selectedType}${dataFieldNum ? ` | ${dataFieldNum} Options` : ''}`;
};

const DataFieldScreenCard = ({ datafield, index }: any) => {
  const [deleteDataField] = useDeleteDataFieldTemplateMutation();

  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [editItem, setEditItem] = useState<any>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  // Transform Quill HTML to proper nested structure
  const transformedDescription = useMemo(
    () => transformQuillListToNested(datafield.description ?? ''),
    [datafield.description]
  );

  const handleDelete = () => {
    setShowDeleteModal(!showDeleteModal);
    deleteDataField(datafield.id);
  };
  const theme = useTheme();

  return (
    <>
      <ModalWidget
        isOpen={!!editItem}
        title="Edit Data Field"
        modalWidth={editItem?.name === "Workflow data field" ? "95%" : undefined}
        children={
          <AddDataField
            isClose={editItem === undefined}
            dataField={editItem}
            toggleEdit={() => null}
            onClose={() => setEditItem(undefined)}
          />
        }
        onClose={() => setEditItem(undefined)}
      />
      <Box
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : index % 2 == 0 ? '#F3F8FC' :'white'
        }}
      >
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
        >
          <Box alignItems="center">
            <Typography
              component="span"
              sx={{
                fontSize: typographyTokens.fontSizes.base,
                fontWeight: typographyTokens.fontWeights.bold,
              }}
            >
              {datafield.name}
            </Typography>
            &nbsp;&nbsp;
            {!!datafield.dataFieldOptions.length && (
              <i
                className={`fa-sharp fa-solid fa-chevron-${
                  showDetail ? 'up' : 'down'
                }`}
                onClick={() => setShowDetail(!showDetail)}
              ></i>
            )}
          </Box>
          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            sx={{
              width: '60px',
            }}
          >
            {/* Delete Button for KnockoutScreen... */}
            <HoveredIconConfirm
              icon="fa-regular fa-trash"
              hoverIcon="fa-solid fa-trash"
              onClick={() => handleDelete()}
            />
            <HoveredIcon
              icon="fa-regular fa-pencil"
              hoverIcon="fa-solid fa-pencil"
              onClick={() => setEditItem(datafield)}
            />
          </Stack>
        </Stack>
        <Typography
          component="div"
          sx={{
            fontSize: typographyTokens.fontSizes.xs,
            fontWeight: typographyTokens.fontWeights.bold,
          }}
        >
          {datafield.starter && 'Starter Screen   '}
          <Typography
            component="span"
            sx={{ fontSize: typographyTokens.fontSizes.xs, color: '#646669' }}
          >
            {calculateTotalProps(datafield)}
          </Typography>
        </Typography>
        <Box
          component="div"
          sx={{
            paddingTop: '15px',
            width: 'fit-content',
            maxWidth: '1200px',
            overflowWrap: 'break-word',
            ...nestedListStyles,
          }}
          dangerouslySetInnerHTML={{ __html: transformedDescription }}
        ></Box>
      </Box>
      <Divider />
      {showDetail &&
        datafield.dataFieldOptions.map((datafieldOption: any, index: any) => {
          return (
            <DataFieldOptionCard
              key={index}
              datafieldOption={datafieldOption}
              index={index}
            />
          );
        })}
    </>
  );
};

export default DataFieldScreenCard;
